import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { Alert as MantineAlert } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo, faTriangleExclamation, faCircleXmark } from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";

import styles from "./alert.module.scss";

type AlertVariant = "disclaimer" | "warning" | "error";

export type AlertProps = {
  variant: AlertVariant;
};

type AlertLinkAsAnchorProps = {
  as?: "a";
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className">;

type AlertLinkAsButtonProps = {
  as: "button";
  onClick: () => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "type" | "onClick">;

export type AlertLinkProps = AlertLinkAsAnchorProps | AlertLinkAsButtonProps;

const variantIconMap: Record<AlertVariant, IconDefinition> = {
  disclaimer: faCircleInfo,
  warning: faTriangleExclamation,
  error: faCircleXmark,
};

const variantClassMap: Record<AlertVariant, string> = {
  disclaimer: styles.variantDisclaimer,
  warning: styles.variantWarning,
  error: styles.variantError,
};

const AlertTitle = ({ children }: PropsWithChildren) => <span className={styles.title}>{children}</span>;

const AlertCaption = ({ children }: PropsWithChildren) => <span className={styles.body}>{children}</span>;

const AlertLink = (props: AlertLinkProps) => {
  if (props.as === "button") {
    const { as: _as, children, ...rest } = props;
    return (
      <button type="button" className={styles.link} {...rest}>
        {children}
      </button>
    );
  }

  const { as: _as, children, ...rest } = props;
  return (
    <a className={styles.link} {...rest}>
      {children}
    </a>
  );
};

const extractTitle = (children: ReactNode): { title: ReactNode | undefined; rest: ReactNode[] } => {
  const childArray = React.Children.toArray(children);
  let title: ReactNode | undefined;
  const rest: ReactNode[] = [];

  for (const child of childArray) {
    if (React.isValidElement<PropsWithChildren>(child) && child.type === AlertTitle && title === undefined) {
      title = child.props.children;
    } else {
      rest.push(child);
    }
  }

  return { title, rest };
};

const AlertBase = ({ variant, children }: PropsWithChildren<AlertProps>) => {
  const { title, rest } = extractTitle(children);

  return (
    <MantineAlert
      title={title}
      icon={<FontAwesomeIcon icon={variantIconMap[variant]} className={styles.icon} />}
      classNames={{
        root: classNames(styles.alert, variantClassMap[variant]),
        wrapper: styles.mantineWrapper,
        icon: styles.mantineIcon,
        body: styles.mantineBody,
        message: styles.content,
        title: styles.title,
      }}
    >
      {rest}
    </MantineAlert>
  );
};

export const Alert = Object.assign(AlertBase, {
  Title: AlertTitle,
  Caption: AlertCaption,
  Link: AlertLink,
});
